import * as SliderPrimitive from '@radix-ui/react-slider';
import { Box, Button, Typography } from '@mui/material';
import formatNumber from 'src/utils/formatNumber';

import type { LeadPackagePrice } from 'src/services/LeadStreamsService/agent';

const money = (value: number) =>
    new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2, style: 'currency', currency: 'RUB' }).format(value);

export default function StreamPackagePicker({
    prices,
    selectedSize,
    onSelect,
    disabled = false,
    monthly = false,
}: {
    prices: LeadPackagePrice[];
    selectedSize: number | null;
    onSelect: (size: number) => void;
    disabled?: boolean;
    monthly?: boolean;
}) {
    if (!prices.length) return null;

    const selectedIndex = Math.max(0, prices.findIndex((price) => price.package_size === selectedSize));
    const selected = prices[selectedIndex];
    const unit = Number(selected.price_per_lead);
    const smallestPackage = prices.reduce((smallest, price) => price.package_size < smallest.package_size ? price : smallest);
    const baseUnit = Number(smallestPackage.one_time_price_per_lead || smallestPackage.price_per_lead);
    const total = unit * selected.package_size;
    const saving = Math.max(0, (baseUnit - unit) * selected.package_size);
    const volumeDiscount = Number(selected.discount_percent || 0);

    return (
        <Box sx={{ border: '1px solid rgba(0, 68, 255, 0.22)', borderRadius: '8px', bgcolor: 'var(--lm-accent-soft)', p: { xs: 2, sm: 2.5 } }}>
            <Typography sx={{ color: 'var(--lm-text)', fontSize: { xs: 18, sm: 20 }, fontWeight: 800 }}>
                {monthly ? 'Сколько лидов получать каждый месяц?' : 'Сколько лидов хотите получить?'}
            </Typography>
            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14, mt: 0.25, mb: 2 }}>
                Лиды появляются в кабинете по мере нахождения.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 210px' }, gap: 2, alignItems: 'center' }}>
                <Box>
                    {prices.length > 1 && (
                        <SliderPrimitive.Root
                            value={[selectedIndex]}
                            min={0}
                            max={prices.length - 1}
                            step={1}
                            disabled={disabled}
                            onValueChange={([index]) => prices[index] && onSelect(prices[index].package_size)}
                            style={{ position: 'relative', display: 'flex', alignItems: 'center', touchAction: 'none', width: '100%', height: 28 }}
                        >
                            <SliderPrimitive.Track style={{ position: 'relative', flexGrow: 1, height: 6, borderRadius: 999, backgroundColor: 'var(--lm-border)' }}>
                                <SliderPrimitive.Range style={{ position: 'absolute', height: '100%', borderRadius: 999, backgroundColor: 'var(--lm-accent-ink)' }} />
                            </SliderPrimitive.Track>
                            <SliderPrimitive.Thumb
                                aria-label="Количество лидов"
                                aria-valuetext={`${formatNumber(selected.package_size)} лидов`}
                                style={{ display: 'block', width: 18, height: 18, borderRadius: '50%', border: '3px solid var(--lm-surface)', backgroundColor: 'var(--lm-accent-ink)', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.16)', cursor: 'grab' }}
                            />
                        </SliderPrimitive.Root>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${Math.min(prices.length, 5)}, minmax(0, 1fr))` }, gap: 0.75, mt: 1.25 }}>
                        {prices.map((price) => {
                            const checked = price.package_size === selected.package_size;
                            return (
                                <Button
                                    key={price.id}
                                    type="button"
                                    aria-pressed={checked}
                                    aria-label={`${formatNumber(price.package_size)} лидов`}
                                    disabled={disabled}
                                    onClick={() => onSelect(price.package_size)}
                                    sx={{ minWidth: 0, minHeight: 46, bgcolor: checked ? 'var(--lm-accent-ink)' : 'var(--lm-surface)', color: checked ? 'var(--lm-surface)' : 'var(--lm-muted)', border: `1px solid ${checked ? 'var(--lm-accent-ink)' : 'var(--lm-border)'}`, borderRadius: '8px', fontSize: 16, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: checked ? 'var(--lm-accent-ink)' : 'var(--lm-canvas)' } }}
                                >
                                    {formatNumber(price.package_size)}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
                <Box sx={{ border: '1px solid var(--lm-border)', borderRadius: '8px', bgcolor: 'var(--lm-surface)', p: 1.5 }}>
                    {[
                        { label: 'Цена за лид', value: money(unit) },
                        ...(volumeDiscount > 0 ? [{ label: 'Скидка за объём', value: `${volumeDiscount}%` }] : []),
                        ...(monthly ? [{ label: 'Автопополнение', value: 'ещё 10%' }] : []),
                        ...(saving > 0 ? [
                            { label: 'Обычная цена', value: money(baseUnit * selected.package_size), strike: true },
                            { label: 'Выгода', value: money(saving), success: true },
                        ] : []),
                        { label: 'Стоимость', value: `${money(total)}${monthly ? '/мес' : ''}`, strong: true },
                    ].map((row) => (
                        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, py: 0.65, borderBottom: row.label === 'Стоимость' ? 0 : '1px solid var(--lm-border)' }}>
                            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 12 }}>{row.label}</Typography>
                            <Typography sx={{ color: 'success' in row && row.success ? 'var(--lm-success-ink)' : 'var(--lm-text)', fontSize: 'strong' in row && row.strong ? 17 : 14, fontWeight: 'strong' in row && row.strong ? 800 : 700, textDecoration: 'strike' in row && row.strike ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>{row.value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
